import { useGenericFormStore } from '@neo/blocks/forms/persistance-store'
import { original } from 'immer'
import { titleize } from 'inflection'
import { difference } from 'lodash'
import { useEffect } from 'react'
import { useNavigate } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import {
  addChunkToQueryCache,
  updateChunkInQueryCache,
  useConversationMutations,
} from 'v2/pages/conversations/mutations/conversations'
import { updateTaskInQueryCache } from 'v2/pages/conversations/mutations/tasks'
import { useActiveConversationsQuery } from 'v2/pages/conversations/queries'
import {
  getConversationURL,
  getLastChildConversation,
  getPubnubTimetoken,
  parseConversationURL,
  updateUnreadCountsForConversation,
} from 'v2/pages/conversations/utils'
import { toChunkModel } from 'v2/pages/conversations/utils/transformers'
import { useSharedConversationStore } from 'v2/state/conversation'
import { usePhoneStore } from 'v2/state/phone'
import { useTabStore } from 'v2/state/tab'
import { Views } from 'v2/types'
import { ConvChannels, type RTask } from 'v2/types/api/conversations'
import { ParticipantState } from 'v2/types/call'
import { ChunkOrigin } from 'v2/types/chunk.external'
import { type Chunk, ChunkSender, ChunkState } from 'v2/types/chunk.internal'
import { PubnubEvents, PubnubStatus } from 'v2/types/pubnub'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { errorToast, infoToast, successToast } from 'v2/utils/toasts'
import { performCallbackWithConversation } from '../utils'
import { notify } from '../utils/browser-notification-manager'
import { hasActiveChatConversation, setLastReadTts } from '../utils/pubnub.util'
import { conversationQueryKeys } from '../utils/query-key-management'
import { usePubNub } from './use-pubnub'

/**
 * usePubNubEffects
 *
 * Effects:
 * 1. Subscribes to active conversations when the pubnub status is connected.
 * 2. Sets the selected conversation to the first conversation in the list when there is no selected conversation.
 * 3. Handles all pubnub events:
 *    - LOGOUT: Unsubscribes from all channels.
 *    - INVITE_AGENT: Invalidates invited conversations query.
 *    - REMOVE_AGENT: Invalidates invited conversations query.
 *    - CONVERSATION_ACCEPTED: Adds active conversation, removes invited conversation, sets selected conversation, subscribes to chat channel.
 *    - CONVERSATION_CLOSED: Removes active conversation.
 *    - CHAT_MESSAGE: Adds message chunk to the conversation.
 */

export const usePubNubEffects = () => {
  const { data: conversations } = useActiveConversationsQuery()
  const { subscribeMutation, pubnubRef, unsubscribeMutation, getLastReadTtsMutation } = usePubNub()
  const pubnubStatus = useTabStore((state) => state.pubnubStatus)
  const navigate = useNavigate()
  // const { setOnReturn } = useSessionStatus()
  const setMeta = useSharedConversationStore((state) => state.setMeta)
  const { addActiveConversation, removeActiveConversation, removeInvitedConversation } =
    useConversationMutations()
  // Subscribe to active conversations when the pubnub status is connected
  useEffect(() => {
    if (
      pubnubStatus === PubnubStatus.CONNECTED &&
      conversations?.conversationsList &&
      conversations.conversationsList.length > 0
    ) {
      const subscribedChannels = pubnubRef.current?.pubnub.getSubscribedChannels() || []
      const chatChannels = conversations.conversationsList
        .filter(hasActiveChatConversation)
        .map((conversation) => conversation.contact_uuid)
      const newChannels = difference(chatChannels, subscribedChannels)
      if (newChannels.length > 0) {
        subscribeMutation.mutate({ channels: newChannels })
      }
      if (pubnubStatus === PubnubStatus.CONNECTED) {
        getLastReadTtsMutation.mutate()
      }
      conversations.conversationsList.forEach((conversation) => {
        updateUnreadCountsForConversation(conversation.conversation_number)
      })
    }
  }, [pubnubStatus, conversations?.conversationsList?.length])

  // Handle all pubnub events
  useEffect(() => {
    const events = [
      primaryEventBus.on(PrimaryEvents.LOGOUT, async () => {
        await setLastReadTts()
      }),

      primaryEventBus.on(PrimaryEvents.APP_ACTIVE, (lastActiveTimestamp) => {
        const subscribedChannels = pubnubRef.current?.pubnub.getSubscribedChannels() || []
        const timetoken = getPubnubTimetoken(lastActiveTimestamp)
        subscribedChannels.forEach((channel) => {
          subscribeMutation.mutate({
            channels: [channel],
            timetoken: channel.startsWith('aom') ? undefined : timetoken,
          })
        })
      }),

      pubnubEventBus.on(PubnubEvents.INVITE_AGENT, (data) => {
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().invited })
        notify.messageRequest(data.contact_name, data.conversation_number)
      }),

      pubnubEventBus.on(PubnubEvents.REMOVE_AGENT, (data) => {
        removeInvitedConversation(data.contact_uuid)
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().invited })
      }),

      pubnubEventBus.on(PubnubEvents.CONVERSATION_ACCEPTED, (data) => {
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().chunks(data.conversation_number),
        })
        addActiveConversation(data)
        removeInvitedConversation(data.contact_uuid)

        if (
          !document.hidden &&
          (data.channel === ConvChannels.CALL ? !data.source_direction_inbound : true)
        ) {
          // Update the contact name in the active call in the phone store if the call is outbound
          if (
            data.channel === ConvChannels.CALL &&
            !data.source_direction_inbound &&
            usePhoneStore.getState().activeCall
          )
            usePhoneStore.getState().setActiveCall({ contactName: data.contact_name })
          navigate(getConversationURL(data.conversation_number, 'active'))
        }
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_SUCCESS_SECONDARY, (data) => {
        addActiveConversation(data)
        removeInvitedConversation(data.contact_uuid)
        // CONV_NAVIGATION_REDIRECT
        if (!document.hidden) navigate(getConversationURL(data.conversation_number, 'active'))
        usePhoneStore.getState().setIsSecondaryParticipant(false)
        usePhoneStore.getState().setOnHold(false)
        usePhoneStore.getState().clearParticipants()

        // OMNI TRANSFER
        // Update the channel in the message form
        // We don't want to update the channel when the call is transferred
        const lastChildChannel = getLastChildConversation(data)?.channel
        if (lastChildChannel !== ConvChannels.CALL)
          useGenericFormStore.getState().partialSet(`${data.conversation_number}-message-form`, {
            channel: lastChildChannel,
          })

        // Feat: CALL MONITORING
        usePhoneStore
          .getState()
          .setMonitoringState(
            data.agent_secondary_conversation_state || data.conversation_other_participant_state,
          )
      }),

      pubnubEventBus.on(PubnubEvents.SECONDARY_PARTICIPANT_HOLD, (data) => {
        usePhoneStore.getState().setOnHold(data.hold)
      }),

      pubnubEventBus.on(PubnubEvents.SECONDARY_PARTICIPANT_ADDED, (data) => {
        const isLiveConv = parseConversationURL().viewId === Views.live
        addActiveConversation(data)
        removeInvitedConversation(data.contact_uuid)
        // CONV_NAVIGATION_REDIRECT
        if (!document.hidden && !isLiveConv)
          navigate(getConversationURL(data.conversation_number, 'active'))
        usePhoneStore.getState().setIsSecondaryParticipant(true)
        usePhoneStore
          .getState()
          .setMonitoringState(
            data.agent_secondary_conversation_state || data.conversation_other_participant_state,
          )
      }),

      pubnubEventBus.on(PubnubEvents.SECONDARY_PARTICIPANT_REMOVED, (data) => {
        removeActiveConversation(data.conversation_number)
        errorToast({
          title: 'Conversation Removed',
          description: `You have been dropped from the conversation`,
        })
      }),

      pubnubEventBus.on(PubnubEvents.RACE_AGENT_JOIN, () => {
        errorToast({
          title: 'Oops',
          description: `Conversation picked up by another agent already`,
        })
      }),

      pubnubEventBus.on(PubnubEvents.CONVERSATION_CLOSED, (data) => {
        // The widget-facing close signal on the shared customer channel carries no
        // eventData, so during a handoff this handler can receive undefined data —
        // ignore it (the agent-channel close, which has eventData, is the real one).
        if (!data) return
        if (data?.contact_uuid) unsubscribeMutation.mutate([data.contact_uuid])
        // Show toast if the conversation is closed by the system and if the user is on the conversation page
        if (parseConversationURL().conversationNumber === data.conversation_number)
          infoToast({
            title: 'Conversation Closed',
            description: `The conversation has been closed by the system`,
          })
        removeActiveConversation(data.conversation_number)
      }),

      pubnubEventBus.on(PubnubEvents.ASYNC_OUTBOUND_FAILED, (data) => {
        errorToast({
          title: `Unable to start a ${data.channel} conversation`,
          description: data.error,
        })
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_CHUNK, (data) => {
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, data.conversation_number)
      }),

      pubnubEventBus.on(PubnubEvents.OUTBOUND_CALL_ENDED, (data) => {
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, data.conversation_number)
      }),

      pubnubEventBus.on(PubnubEvents.CALL_RECORDING, (data) => {
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, data.conversation_number)
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_SUCCESS_PRIMARY, () => {
        // TODO: Replce with just modifying the conversation in the cache
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_INITIATED_PRIMARY, (data) => {
        // TODO: Replce with just modifying the conversation in the cache
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
        const connectingParticipant = usePhoneStore
          .getState()
          .participants.find((p) => p.state === ParticipantState.CONNECTING)

        // Update the participant to connected
        usePhoneStore
          .getState()
          .updateParticipant(
            connectingParticipant?.member || data.agent_sip || data.participant_identifier,
            {
              state: ParticipantState.CONNECTED,
              member: data.agent_sip || data.participant_identifier,
              identifier: data.agent_name || data.participant_identifier,
            },
          )
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_FAILED, () => {
        // TODO: Replace with just modifying the conversation in the cache
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
        usePhoneStore.getState().clearParticipants()
      }),

      pubnubEventBus.on(PubnubEvents.TRANSFER_SECONDARY_LEFT, () => {
        // TODO: Replace with just modifying the conversation in the cache
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
        usePhoneStore.getState().clearParticipants()
      }),

      // When call is merged, applicable only for warm call transfers, comes only to agent 2
      pubnubEventBus.on(PubnubEvents.TRANSFER_MERGE_SUCCESS, (data) => {
        performCallbackWithConversation(data.conversation_number, (c) => {
          usePhoneStore.getState().setOnHold(false)
          usePhoneStore.getState().addParticipant({
            identifier: c.primary_agent_name,
            member: c.contact_uuid,
            startAt: new Date().toISOString(),
            state: ParticipantState.MERGED,
            participantType: null,
            isOnHold: false,
          })
        })
      }),

      pubnubEventBus.on(PubnubEvents.CHUNK_ADDED, (data) => {
        if (data.conversation_number)
          addChunkToQueryCache(
            toChunkModel(data, ChunkOrigin.apiFetchChunks),
            data.conversation_number,
          )
      }),
      pubnubEventBus.on(PubnubEvents.MESSAGE_NOTIFICATION, (data) => {
        let c: Chunk | undefined
        updateChunkInQueryCache(
          data.conversation_number,
          (chunk) => chunk.id === data.message_id,
          (chunk) => {
            c = original(chunk)
            return {
              meta: {
                ...chunk.meta,
                state: data.status,
                stateReason: data.error_data?.comments || data.error_data?.description,
              },
            }
          },
        )

        // Add error meta to conversation if there's an error
        if (data.error_data && c && data.error_data.errorType === 'Whole conversation') {
          setMeta(data.conversation_number, {
            error: {
              type: data.error_data.errorType as 'Whole conversation' | 'Specific message',
              message: data.error_data.comments || data.error_data.description,
              channel: c?.channel,
            },
          })
        }
      }),
      pubnubEventBus.on(PubnubEvents.TASK_PENDING, (data) => {
        const agentName = titleize(data?.task?.destination_identifier_name)
        infoToast({
          title: '',
          description: `Task is assigned to ${agentName}`,
        })
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, String(data.conversation_number))
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
      }),
      pubnubEventBus.on(PubnubEvents.TASK_ACTIVITY_CREATED, (data) => {
        updateChunkInQueryCache(
          String(data.conversation_number),
          (chunk) => (chunk.meta as RTask)?.uuid === data?.task?.uuid,
          () => ({
            meta: data?.task,
          }),
        )
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().taskActivities(data?.task?.uuid),
        })
      }),
      pubnubEventBus.on(PubnubEvents.TASK_ACCEPTED, (data) => {
        const agentName = titleize(data?.task?.destination_identifier_name)
        successToast({
          title: '',
          description: `Task is accepted by ${agentName}`,
        })
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, String(data.conversation_number))
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().active,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().taskActivities(data?.task?.uuid),
        })
      }),
      pubnubEventBus.on(PubnubEvents.TASK_FAILED, (data) => {
        const agentName = titleize(data?.task?.destination_identifier_name)
        errorToast({
          title: '',
          description: `Failed to assign task to ${agentName}`,
        })
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, String(data.conversation_number))
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().taskActivities(data?.task?.uuid),
        })
      }),
      pubnubEventBus.on(PubnubEvents.TASK_DECLINED, (data) => {
        const agentName = titleize(data?.task?.destination_identifier_name)
        errorToast({
          title: '',
          description: `Task is declined by ${agentName}`,
        })
        const chunk = toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks)
        addChunkToQueryCache(chunk, String(data.conversation_number))
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().taskActivities(data?.task?.uuid),
        })
      }),
      pubnubEventBus.on(PubnubEvents.TASK_ROUTING_STATUS_UPDATED, (data) => {
        updateChunkInQueryCache(
          String(data.conversation_number),
          (chunk) => (chunk.meta as RTask)?.uuid === data?.task?.uuid,
          () => ({
            meta: data?.task,
          }),
        )
        updateTaskInQueryCache({
          conversationId: String(data.conversation_number),
          operation: 'update',
          taskUuid: data?.task?.uuid,
          data: data?.task,
        })
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys().taskActivities(data?.task?.uuid),
        })
      }),
    ]

    return () => {
      events.forEach((unbind) => unbind())
    }
  }, [])

  // Dependency: conversations
  useEffect(() => {
    // Subscribe to chat messages
    // TODO_BE_V2: Send conversation number in the event data
    const events = [
      pubnubEventBus.on(PubnubEvents.CHAT_MESSAGE, (data) => {
        if (data.sender === ChunkSender.CUSTOMER && data.contactUuid) {
          const conversation = conversations?.contactUuidMap[data.contactUuid]
          if (conversation) notify.chatMessage(data, conversation)
        }
        if (data.contactUuid) {
          const conversation = conversations?.contactUuidMap[data.contactUuid]
          if (conversation) {
            addChunkToQueryCache(data, conversation.conversation_number)
          }
        }
      }),
      pubnubEventBus.on(PubnubEvents.MESSAGE_RECEIVED, (data) => {
        const chunk = toChunkModel(data, ChunkOrigin.apiFetchChunks)
        if (data.conversation_number) {
          addChunkToQueryCache(chunk, data.conversation_number)
          if (chunk.sender === ChunkSender.CUSTOMER && chunk.contactUuid) {
            const conversation = conversations?.contactUuidMap[chunk.contactUuid]
            if (conversation) notify.chatMessage(chunk, conversation)
          }
        }
      }),
      pubnubEventBus.on(PubnubEvents.MESSAGE_ACTION, (data) => {
        const conversation = conversations?.contactUuidMap[data.channel]
        // Update the chunk state to read
        if (conversation && data.data?.value) {
          updateChunkInQueryCache(
            conversation.conversation_number,
            (chunk) => chunk.timetoken === Number(data.data.messageTimetoken),
            (chunk) => ({
              meta: {
                ...chunk.meta,
                state: ChunkState.DELIVERED,
              },
            }),
          )
        }
      }),
    ]

    return () => {
      events.forEach((unbind) => unbind())
    }
  }, [conversations?.conversationsList?.length])
}
